import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { contacts, inbox } from '@repletoryreach/sdk-api';
import { Badge, Button, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { ContactDrawer, type DrawerTarget } from '../../contacts/components/ContactDrawer';
import { formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { useAgentOptions } from './TransferModal';

export interface ContactContextProps {
  conversationId: string;
  msisdn: string;
  displayName: string | null | undefined;
}

/**
 * Who the agent is actually talking to.
 *
 * `inbox.md` §4 item 17: the thread header showed a name and a phone number and
 * nothing else, so checking a contact's tags, language or opt-in state meant
 * leaving the conversation. Everything below already existed in the contacts
 * service; the inbox simply never asked for it.
 *
 * The body of the "Contact" section of the settings sidebar, which supplies
 * the title. The hand-off history underneath answers "who had this before
 * me?", which `listTransfers()` has always returned and nothing rendered.
 */
export function ContactContext({ conversationId, msisdn, displayName }: ContactContextProps) {
  const t = useT();
  const qc = useQueryClient();
  // The contact's own drawer, over the conversation (owner, 15 October 2026): adding someone who wrote in, or looking
  // at someone already in the book, never leaves the chat.
  const [drawer, setDrawer] = useState<DrawerTarget>(null);
  // What the conversation already knows about a new contact: an address on an email thread, a number on a phone
  // channel, nothing on Messenger or Instagram (their id is not a number). Their name as the channel gave it.
  const ref = msisdn?.trim() ?? '';
  const prefill = {
    name: displayName?.trim() || null,
    email: ref.includes('@') ? ref : null,
    msisdn: /^\+?[0-9][0-9 ()-]{5,}$/.test(ref) ? ref : null
  };

  // Search by msisdn: the conversation carries no contact id, and adding one
  // would mean a schema change for a lookup the contact book already supports.
  const contact = useQuery({
    queryKey: ['contact', 'by-msisdn', msisdn],
    queryFn: () => contacts.listContacts({ search: msisdn, limit: 1 }),
    select: (p) => p.data?.[0] ?? null,
    staleTime: 60_000
  });

  const transfers = useQuery({
    queryKey: ['conversation', conversationId, 'transfers'],
    queryFn: () => inbox.listTransfers(conversationId),
    select: (r) => r.data ?? [],
    staleTime: 30_000
  });

  const found = contact.data;

  const { options: agents } = useAgentOptions();
  const nameOf = (userId: string | null) => {
    if (!userId) return t('inbox.unassigned');
    return agents.find((a) => a.user_id === userId)?.label ?? t('inbox.anotherAgent');
  };

  return (
    <Stack gap="md">
      {contact.isPending && <Skeleton height={48} />}

      {!contact.isPending && !found && (
        <Stack gap={4}>
          <Text role="bodySmall" fw={600}>{displayName ?? msisdn}</Text>
          {/* Not an error: plenty of people who message in are not in the
              book yet, and adding them is the useful next step. */}
          <Text role="caption" c="var(--rr-color-text-muted)">{t('inbox.context.notInBook')}</Text>
          <div>
            <Button size="xs" variant="light" onClick={() => setDrawer('new')} data-testid="inbox-add-contact">
              {t('inbox.context.addContact')}
            </Button>
          </div>
        </Stack>
      )}

      {found && (
        <Stack gap={6}>
          {/* Opens the contact's drawer over the conversation rather than going to the contact list. */}
          <button type="button" className="rr-inbox2__context-link rr-inbox2__context-button" onClick={() => setDrawer(found)} data-testid="inbox-open-contact">
            {found.name ?? msisdn}
          </button>
          <Text role="caption" c="var(--rr-color-text-muted)">{found.msisdn}</Text>

          <Group gap={6} wrap="wrap">
            {/* Opt-in decides whether a broadcast will ever reach them, so
                it belongs where the agent can see it. */}
            <Badge size="xs" variant="light" color={found.opt_in ? 'green' : 'gray'}>
              {found.opt_in ? t('contacts.optedIn') : t('contacts.optedOut')}
            </Badge>
            {found.locale && <Badge size="xs" variant="light">{found.locale}</Badge>}
            {(found.tags ?? []).map((tag) => (
              <Badge key={tag} size="xs" variant="light">{tag}</Badge>
            ))}
          </Group>
        </Stack>
      )}

      {(transfers.data?.length ?? 0) > 0 && (
        <div className="rr-inbox2__side-field">
          <Text role="caption" className="rr-inbox2__side-label">{t('inbox.context.handoffs')}</Text>
          {transfers.data!.slice(0, 5).map((tr) => (
            <Stack key={tr.id} gap={1}>
              <Text role="bodySmall">
                {/* The endpoint returns bare user UUIDs. Resolved against the
                    same agent list the assign picker uses, because "handed
                    over by a1b2c3d4…" tells nobody anything. */}
                {t('inbox.context.handoff', {
                  from: nameOf(tr.from_user_id),
                  to: nameOf(tr.to_user_id)
                })}
              </Text>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {formatRelative(tr.occurred_at)}
                {tr.reason ? ` · ${tr.reason}` : ''}
              </Text>
            </Stack>
          ))}
        </div>
      )}
      <ContactDrawer
        contact={drawer}
        prefill={prefill}
        onClose={() => setDrawer(null)}
        // The panel shows the contact as soon as it is in the book.
        onSaved={() => qc.invalidateQueries({ queryKey: ['contact', 'by-msisdn', msisdn] })}
      />
    </Stack>
  );
}
